import { HwTypeKey } from "@const/hwTypeKey.constants";
import { SoftLimit } from "@dto/backendResponse.dto";
import { BaseDeviceProps } from "@hostcfgs/deviceProps";
import { Card, Checkbox, Col, Form, FormInstance, Input, InputNumber, Row, Select } from "antd";
import { FormItemProps } from 'antd/es/form/FormItem';
import { NamePath } from "antd/es/form/interface";
import { Component, ReactNode } from 'react';
import { XmSelect } from "src/component/select/XmSelect";
import { HostCfgCardProps } from "src/data/props";
import { GLOB } from "src/util/Glob";
import { nameof } from "ts-simple-nameof";
import { HostCfgItem, PROTO } from "../ConfigurationTypes";

function path(selector: (props: HostCfgItem) => unknown) {
  return nameof<HostCfgItem>(selector).split('.');
}

export abstract class AbstractDevice<P extends BaseDeviceProps, S = unknown> extends Component<{ cfg: HostCfgItem<P>, form: FormInstance, editing: boolean; }, S> {
  protected abstract readonly dataClass: new () => P;
  protected readonly abortCtrl = new AbortController();

  static freeLicenseLimitDialog() {
    GLOB.app.modal.warning({
      title: 'License restriction',
      width: "50em",
      content: <div>
        You have reached the limit of devices that can be monitored by the free edition of XorMon.<br />
        Contact us to get a trial license to fully test all the features of XorMon.<br />
        Use <a href='mailto:sales@xorux.com' target='_blank'>sales@xorux.com</a> or the <a href="https://xormon.com/trial-form.php" target="_blank">web form</a>.
      </div>,
      okText: 'Close'
    });
  }

  static softLimitDialog(softLimit: SoftLimit) {
    GLOB.app.modal.warning({
      title: 'License restriction',
      width: "50em",
      content: <div>
        You have reached the limit of {softLimit.limit} {softLimit.subsystem}s that your XorMon license can cover.<br />
        Contact us to upgrade your XorMon license.<br />
        Use <a href='mailto:sales@xorux.com' target='_blank'>sales@xorux.com</a> or the <a href="https://xormon.com/quote-form-ng.php" target="_blank">web form</a>.
      </div>,
      okText: 'Close'
    });
  }

  static newDataDialog() {
    GLOB.app.modal.info({
      mask: { closable: true },
      title: 'New device data',
      content: <div>
        <p>
          The initial download of configuration and performance data
          from a newly added device can take up to one hour.
        </p>
        <p>Please wait at least 60 minutes and then reload the page.</p>
        <p>You can use the application as usual in the meantime.</p>
      </div>
    });
  }

  static findKey(obj, partKey: string, parents: string[]): { name: string[], value; } {
    if (typeof obj === 'object') {
      for (const key in obj) {
        if (Object.hasOwn(obj, key)) {
          const element = obj[key];
          if (key.toLowerCase().includes(partKey) && typeof element !== 'object') {
            return { name: [...parents, key], value: element };
          }
          const result = AbstractDevice.findKey(element, partKey, [...parents, key]);
          if (result) return result;
        }
      }
    }
  }

  static removeWhitespace(value: string, namePath: NamePath, form: FormInstance) {
    const trimmed = value?.replace(/\s/g, '');
    if (trimmed != value) {
      form.setFieldValue(namePath, trimmed);
    }
  }

  static getIgnoreHScard(item: HostCfgItem) {
    return <Card {...HostCfgCardProps} >
      <Row>
        <Col span={8}>
          <Form.Item labelCol={{ span: 20 }} wrapperCol={{ span: 4 }} label="Ignore health status" name={path(t => t.ignore_health_status)} valuePropName="checked">
            <Checkbox />
          </Form.Item>
        </Col>
        <Col span={16}>
          <Form.Item labelCol={{ span: 5 }} wrapperCol={{ span: 19 }} label="Reason" name={path(t => t.ignore_health_status_reason)}
            rules={[{ required: item?.ignore_health_status, whitespace: true }]}>
            <Input disabled={!item?.ignore_health_status} />
          </Form.Item>
        </Col>
      </Row>
    </Card>;
  }

  componentWillUnmount(): void {
    this.abortCtrl.abort();
  }

  public createItem() {
    const item = new HostCfgItem<P>();
    item.data = new this.dataClass();
    return item;
  }

  protected path(selector: (props: HostCfgItem<P>) => unknown) {
    return nameof<HostCfgItem<P>>(selector).split('.');
  }

  public static protoSelect() {
    return <XmSelect ignoreReadonly>
      {Object.entries(PROTO).map(([key, val]) => <Select.Option key={key} value={val}>{val}</Select.Option>)}
    </XmSelect>;
  }

  protected getProtoSelect() {
    return AbstractDevice.protoSelect();
  }

  static getPort(label: string, name: NamePath, props?: FormItemProps) {
    return <Form.Item {...props} label={label} name={name}
      rules={[{ required: true, whitespace: true, type: 'number', min: 0, max: 65535 }]}>
      <InputNumber min={0} max={65535} />
    </Form.Item>;
  }

  protected getPort(label: string, name: NamePath, props?: FormItemProps) {
    return AbstractDevice.getPort(label, name, props);
  }

  // TODO: hwTypes are no longer array, can be simplified?
  abstract getNewHwType(): HwTypeKey;

  abstract render(): ReactNode;
}

